<template>
  <div class="drawing-canvas-toolbar">
    <div class="drawing-tools">
      <div class="tools-picker">
        <span
          v-for="tool in tools"
          :key="tool.name"
          :ref="(el) => setToolRef(tool.name, el)"
          :class="{ selected: isSelectedTool(tool.name) }"
          class="tool"
          @click="handleChangeTool(tool.name)"
        >
          <DrawingToolsIcon v-if="isSelectedTool(tool.name)" :name="selectedToolIconName" />
          <DrawingToolsIcon v-else :name="toolIconName(tool.name)" />
          <span v-if="showConfigArrow(tool.name)" class="config-arrow">
            <svg-icon name="ArrowRight16" kind="action" />
          </span>
        </span>
      </div>
    </div>

    <!-- Config Popup -->
    <drawing-tool-config-popup
      :visible="showConfigPopup"
      :reference-element="configPopupRef"
      :selected-tool="selectedTool"
      :selected-color="selectedColor"
      :selected-thickness="selectedThickness"
      :colors="colors"
      @on-close="handleConfigPopupClose"
      @on-color-change="handleColorChange"
      @on-thickness-change="handleThicknessChange"
    />
    <div
      ref="actionsWrapper"
      class="drawing-actions-wrapper"
      :class="{ scrollable: isActionScrollable }"
    >
      <div class="drawing-actions" @scroll="handleActionScroll">
        <button
          type="button"
          class="button select"
          :class="{ selected: isSelectedTool('select') }"
          :disabled="!canSelect"
          @click="handleSelect"
        >
          <svg-icon name="Select24" kind="action" />
          <span class="label">{{ t("tinymce_plugin_drawing_action_button_select") }}</span>
        </button>
        <button
          type="button"
          class="button move"
          :class="{ selected: isSelectedTool('move') }"
          @click="handleMove"
        >
          <svg-icon name="Hand24" kind="action" />
          <span class="label">{{ t("tinymce_plugin_drawing_action_button_move") }}</span>
        </button>
        <button type="button" class="button undo" :disabled="!canUndo" @click="handleUndo">
          <svg-icon name="Undo24" kind="action" />
          <span class="label">{{ t("tinymce_plugin_drawing_action_button_undo") }}</span>
        </button>
        <button type="button" class="button redo" :disabled="!canRedo" @click="handleRedo">
          <svg-icon name="Redo24" kind="action" />
          <span class="label">{{ t("tinymce_plugin_drawing_action_button_redo") }}</span>
        </button>
        <button type="button" class="button remove" :disabled="!canRemove" @click="handleRemove">
          <svg-icon name="Trash24" kind="action" />
          <span class="label">{{ t("tinymce_plugin_drawing_action_button_delete") }}</span>
        </button>
        <button type="button" class="button forward" :disabled="!canForward" @click="handleForward">
          <svg-icon name="LayerUp24" kind="action" />
          <span class="label">{{ t("tinymce_plugin_drawing_action_button_forward") }}</span>
        </button>
        <button
          type="button"
          class="button backward"
          :disabled="!canBackward"
          @click="handleBackward"
        >
          <svg-icon name="LayerDown24" kind="action" />
          <span class="label">{{ t("tinymce_plugin_drawing_action_button_backward") }}</span>
        </button>
        <button type="button" class="button clear" :disabled="!canClear" @click="handleClear">
          <svg-icon name="Reset24" kind="action" />
          <span class="label">{{ t("tinymce_plugin_drawing_action_button_clear_all") }}</span>
        </button>
      </div>
    </div>
    <div class="zoom-actions">
      <tooltip-button
        class="button zoom-fit"
        kind="ghost"
        size="small"
        icon-only
        :show-tooltip="true"
        :disabled="false"
        tip="Fit to Width"
        @click="handleZoomFit"
      >
        <svg-icon name="FitToWidth24" kind="action" />
      </tooltip-button>

      <button type="button" class="button zoom-out" :disabled="isZoomMin" @click="handleZoomOut">
        <svg-icon name="ZoomOut24" kind="action" />
      </button>

      <!-- Native input used instead of <cv-slider> as the latter doesn't support dragging on Mobile devices -->
      <input
        class="zoom-slider"
        type="range"
        :value="zoomPercentage"
        :min="minZoom"
        :max="maxZoom"
        @change="handleZoomSliderChange"
      />

      <button type="button" class="button zoom-in" :disabled="isZoomMax" @click="handleZoomIn">
        <svg-icon name="ZoomIn24" kind="action" />
      </button>

      <button type="button" class="button zoom-label" @click="handleZoomDefault">
        <span class="label">{{ zoomSizeLabel }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import DrawingToolsIcon from "@plugins/drawing/components/DrawingToolsIcon.vue";
import DrawingToolConfigPopup from "@plugins/drawing/components/DrawingToolConfigPopup.vue";
import { DRAWING_TOOL, CANVAS_ACTION, CANVAS_SIZE } from "@plugins/drawing/drawing.constants";
import DOMUtils from "@utils/dom.utils";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { ToolConfig, BrushConfig, EraserConfig, TextBoxConfig } from "@plugins/drawing/types.ts";

interface Tool {
  name: string;
  [key: string]: unknown;
}

interface Color {
  name: string;
  value: string;
  [key: string]: unknown;
}

interface Props {
  tools?: Tool[];
  colors?: Color[];
  selectedTool?: string;
  penConfig: BrushConfig;
  highlighterConfig: BrushConfig;
  eraserConfig: EraserConfig;
  textBoxConfig: TextBoxConfig;
  zoomPercentage: number;
  isZoomMin: boolean;
  isZoomMax: boolean;
  canSelect: boolean;
  canUndo: boolean;
  canRedo: boolean;
  canRemove: boolean;
  canForward: boolean;
  canBackward: boolean;
  canClear: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  tools: () => [],
  colors: () => [],
  selectedTool: "",
});

const emit = defineEmits<{
  toolSelected: [data: { tool: string; value: string }];
  drawingAction: [action: string, value?: unknown];
  toolConfigChange: [data: ToolConfig];
}>();

const { t } = useI18n();

const isActionScrollable = ref(false);
const actionsWrapperRef = useTemplateRef("actionsWrapper");

const showConfigPopup = ref(false);
const configPopupRef = ref<HTMLElement | null>(null);
const toolRefs = ref<Record<string, HTMLElement>>({});

/** Tracks which tools show the arrow indicator (hints that second click opens config popup). */
const toolArrowState = ref<Record<string, boolean>>({});
const lastClickedTool = ref<string>("");

const isConfigurableTool = (toolName: string) => {
  return (
    toolName === DRAWING_TOOL.PEN ||
    toolName === DRAWING_TOOL.HIGHLIGHTER ||
    toolName === DRAWING_TOOL.ERASER ||
    toolName === DRAWING_TOOL.TEXT_BOX
  );
};

const selectedColor = computed(() => {
  if (props.selectedTool === DRAWING_TOOL.TEXT_BOX) {
    return props.textBoxConfig.color;
  }
  if (props.selectedTool === DRAWING_TOOL.HIGHLIGHTER) {
    return props.highlighterConfig.color;
  }
  return props.penConfig.color;
});

const selectedThickness = computed(() => {
  if (props.selectedTool === DRAWING_TOOL.ERASER) {
    return props.eraserConfig.width;
  }
  if (props.selectedTool === DRAWING_TOOL.HIGHLIGHTER) {
    return props.highlighterConfig.width;
  }
  return props.penConfig.width;
});

const selectedToolIconName = computed(() => {
  const capitalisedToolName =
    props.selectedTool.charAt(0).toUpperCase() + props.selectedTool.slice(1);
  const getColor = (colorValue?: string) => props.colors.find((c) => c.value === colorValue)?.name;
  switch (props.selectedTool) {
    case DRAWING_TOOL.PEN:
      return `${capitalisedToolName}${getColor(props.penConfig.color)}`;
    case DRAWING_TOOL.HIGHLIGHTER:
      return `${capitalisedToolName}${getColor(props.highlighterConfig.color)}`;
    default: {
      return `${capitalisedToolName}Active`;
    }
  }
});

const minZoom = computed(() => CANVAS_SIZE.ZOOM_MIN);

const maxZoom = computed(() => CANVAS_SIZE.ZOOM_MAX);

const zoomSizeLabel = computed(() =>
  t("tinymce_plugin_drawing_modal_zoom_size_label", {
    zoomSize: props.zoomPercentage,
  }),
);

watch(
  () => props.selectedTool,
  (newTool) => {
    if (newTool === CANVAS_ACTION.SELECT || newTool === CANVAS_ACTION.MOVE) {
      showConfigPopup.value = false;
    }
  },
);

const isSelectedTool = (tool) => {
  return props.selectedTool === tool;
};

const handleChangeTool = (toolName: string) => {
  if (isSelectedTool(toolName) && isConfigurableTool(toolName)) {
    // Second click on same tool - show config popup
    configPopupRef.value = toolRefs.value[toolName];
    showConfigPopup.value = true;
    return;
  }

  // First click or different tool selected
  if (isConfigurableTool(toolName)) {
    // Show arrow indicator
    toolArrowState.value[toolName] = true;
    lastClickedTool.value = toolName;

    // Hide arrow for other tools
    Object.keys(toolArrowState.value).forEach((key) => {
      if (key !== toolName) {
        toolArrowState.value[key] = false;
      }
    });
  }

  // Close popup if open
  showConfigPopup.value = false;

  emit("toolSelected", {
    tool: "drawingTools",
    value: toolName,
  });
};

const handleSelect = () => {
  emit("drawingAction", CANVAS_ACTION.SELECT);
};

const handleMove = () => {
  emit("drawingAction", CANVAS_ACTION.MOVE);
};

const handleUndo = () => {
  emit("drawingAction", CANVAS_ACTION.UNDO);
};

const handleRedo = () => {
  emit("drawingAction", CANVAS_ACTION.REDO);
};

const handleRemove = () => {
  emit("drawingAction", CANVAS_ACTION.DELETE);
};

const handleForward = () => {
  emit("drawingAction", CANVAS_ACTION.FORWARD);
};

const handleBackward = () => {
  emit("drawingAction", CANVAS_ACTION.BACKWARD);
};

const handleClear = () => {
  emit("drawingAction", CANVAS_ACTION.CLEAR);
};

const handleZoomFit = () => {
  emit("drawingAction", CANVAS_ACTION.ZOOM_FIT);
};

const handleZoomIn = () => {
  emit("drawingAction", CANVAS_ACTION.ZOOM_IN);
};

const handleZoomOut = () => {
  emit("drawingAction", CANVAS_ACTION.ZOOM_OUT);
};

const handleZoomDefault = () => {
  emit("drawingAction", CANVAS_ACTION.ZOOM_DEFAULT);
};

const handleZoomSliderChange = (e) => {
  emit("drawingAction", CANVAS_ACTION.ZOOM_SLIDE, e.target.value);
};

const toolIconName = (tool) => {
  return `${tool}Default`;
};

const setToolRef = (toolName: string, el: unknown) => {
  if (el) {
    toolRefs.value[toolName] = el as HTMLElement;
  }
};

const showConfigArrow = (toolName: string) => {
  return isConfigurableTool(toolName) && isSelectedTool(toolName) && toolArrowState.value[toolName];
};

const handleConfigPopupClose = () => {
  showConfigPopup.value = false;
};

const emitToolConfigChange = (changes: { color?: string; thickness?: number }) => {
  const getConfigForTool = (): BrushConfig | EraserConfig | TextBoxConfig => {
    switch (props.selectedTool) {
      case DRAWING_TOOL.TEXT_BOX:
        return { color: changes.color ?? props.textBoxConfig.color };

      case DRAWING_TOOL.ERASER:
        return { width: changes.thickness ?? props.eraserConfig.width };

      case DRAWING_TOOL.HIGHLIGHTER:
        return {
          width: changes.thickness ?? props.highlighterConfig.width,
          color: changes.color ?? props.highlighterConfig.color,
        };

      default: // PENCIL
        return {
          width: changes.thickness ?? props.penConfig.width,
          color: changes.color ?? props.penConfig.color,
        };
    }
  };

  emit("toolConfigChange", {
    type: props.selectedTool,
    config: getConfigForTool(),
  });
};

const handleColorChange = (color: string) => {
  emitToolConfigChange({ color });
};

const handleThicknessChange = (thickness: number) => {
  emitToolConfigChange({ thickness });
};

const checkIfActionScrollable = () => {
  const drawingActions = DOMUtils.el(".drawing-actions");
  isActionScrollable.value = drawingActions.scrollWidth > drawingActions.clientWidth;
};

const handleActionScroll = (e) => {
  if (e.target.scrollLeft > 0) {
    DOMUtils.addClass(actionsWrapperRef.value, "scroll-start");
  } else {
    DOMUtils.removeClass(actionsWrapperRef.value, "scroll-start");
  }

  if (Math.round(e.target.scrollLeft) + e.target.clientWidth >= e.target.scrollWidth) {
    DOMUtils.addClass(actionsWrapperRef.value, "scroll-end");
  } else {
    DOMUtils.removeClass(actionsWrapperRef.value, "scroll-end");
  }
};

onMounted(() => {
  // Initialize arrow state for default selected tool if it's configurable
  if (props.selectedTool && isConfigurableTool(props.selectedTool)) {
    toolArrowState.value[props.selectedTool] = true;
  }

  checkIfActionScrollable();
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.drawing-canvas-toolbar {
  .tools-picker {
    @include media("tablet-min") {
      display: flex;
      flex-flow: row wrap;
      justify-content: center;
    }

    span {
      user-select: none;
      cursor: pointer;
    }
  }

  .tools-picker {
    display: flex;
    align-items: center;

    @include media("mobile") {
      position: absolute;
      top: 0;
      align-items: flex-start;

      .tool {
        display: inline-block;
        margin: 0 $spacing-03;
        flex-shrink: 0;
      }
    }

    @include media("tablet-min") {
      flex-flow: column;
    }
    .tool {
      height: 60px;
      min-width: 40px;
      position: relative;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      flex-shrink: 0;

      &:nth-child(1),
      &:nth-child(2) {
        @include media("tablet-min") {
          height: 4rem;
        }
      }

      &:nth-child(1),
      &:nth-child(3) {
        .drawing-tools-icon {
          transform: translateY(10px);
        }
      }

      &:nth-child(1) {
        @include media("mobile") {
          width: 42px;
        }
        @include media("tablet-min") {
          .drawing-tools-icon {
            transform: rotate(270deg);
          }
        }
      }

      &:nth-child(2) {
        @include media("mobile") {
          width: 56px;
        }
        .drawing-tools-icon {
          transform: translateY(15px);
          @include media("tablet-min") {
            transform: rotate(270deg) translateY(5px);
          }
        }

        .config-arrow {
          @include media("mobile") {
            left: 18px;
          }
        }
      }

      &:nth-child(3) {
        .drawing-tools-icon {
          transform: rotate(180deg) translateY(-10px);
          @include media("tablet-min") {
            transform: rotate(90deg) translateY(0);
          }
        }
      }

      .config-arrow {
        position: absolute;
        @include media("mobile") {
          bottom: -2rem;
          left: 10px;
          transform: rotate(90deg);
        }
        @include media("tablet-min") {
          height: 16px;
          right: -26px;
        }

        .sls-icon {
          [fill] {
            fill: $interactive-01;
          }
        }
      }
    }
  }

  .color-picker {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: $spacing-03;

    span {
      display: inline-block;
      width: 48px;
      height: 48px;
      padding: $spacing-01;
      position: relative;
      outline: 1px solid $toolbar-background-01;
      border-radius: 50%;
      background-clip: content-box;

      &.White {
        box-shadow: inset 0 0 0 1px $ui-06;

        &.selected {
          box-shadow: none;
        }
      }

      &.selected {
        outline: 2px solid $interactive-01;
        border-color: $interactive-04;

        &::before {
          content: "";
          display: inline-block;
          width: 2rem;
          height: 2rem;
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          border-radius: 50%;
          background-color: $ui-01;
          z-index: -1;
        }
      }
    }
  }

  .drawing-actions-wrapper {
    position: relative;
    margin-bottom: $spacing-05;
    background-color: $ui-06;
    border-radius: 10px;
    overflow: hidden;
    @include media("tablet-min") {
      position: absolute;
      top: 0;
      right: 19.25rem;
      width: calc(100% - 27.25rem);
      z-index: z-map("default");
    }

    &.scrollable {
      &::after {
        visibility: visible;
      }
      &.scroll-end {
        &::after {
          visibility: hidden;
        }
      }
    }

    &.scroll-start {
      &::before {
        visibility: visible;
      }
    }

    &::before,
    &::after {
      content: "";
      position: absolute;
      top: -0.5rem; // padding top of content. padding is added on content so that the scrollbar will appear below
      width: 2.5rem;
      height: 100%;
      background: linear-gradient(180deg, #e4e8ee 4.69%, rgba(228, 232, 238, 0) 100%);
      pointer-events: none;
      visibility: hidden;
    }

    &::before {
      left: 0;
      transform: rotate(-90deg);
    }

    &::after {
      right: 0;
      transform: rotate(90deg);
    }
  }

  .drawing-actions {
    display: flex;
    overflow-x: auto;
    padding: $spacing-03;

    .button {
      @include carbon--type-style("button-01");
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      color: $icon-02;

      &:not(:first-of-type) {
        margin-left: $spacing-06;
      }

      &:last-of-type {
        border-left: $divider-01;
        padding-left: $spacing-05;
      }

      &.selected {
        color: $icon-01;
      }

      .label {
        margin-left: $spacing-03;
      }

      .sls-icon {
        min-width: 24px;

        [fill] {
          fill: currentColor;
        }

        [stroke] {
          stroke: currentColor;
        }
      }
    }
  }

  .zoom-actions {
    display: flex;
    align-items: center;
    top: 0;
    right: 0;
    height: 40px;

    button {
      display: flex;
      align-items: center;
      justify-content: center;

      &.zoom-fit {
        margin-right: $spacing-03;
      }

      &.zoom-label {
        justify-content: flex-end;
        // Fixed width so that the zoom actions doesn't jump when the zoom size changes, causing bad UX
        width: 5rem;
        margin-left: $spacing-05;
        text-transform: uppercase;
      }
    }

    .zoom-label {
      @include carbon--type-style("label-03", true);
      color: $text-02;
    }

    @include media("mobile") {
      display: none;
    }
  }
}
</style>
